/**
 * Firebase Web Push configuration — Super-admin only.
 *
 * The FCM pipeline is already live (backend `push.py`, frontend
 * `features/push/firebase.ts`, `firebase-messaging-sw.js`). This screen
 * lets an admin paste the 8 required values without touching .env by hand.
 * The service-account JSON is stored server-side in `app_settings.firebase`
 * and NEVER re-emitted to the client — after save the API only returns a
 * masked preview (first 4 / last 4).
 *
 * Frontend-side keys still need to be baked into `REACT_APP_FIREBASE_*` at
 * build time for the customer PWA + `firebase-messaging-sw.js`. This screen
 * stores them centrally so the deploy step can read them from Mongo instead
 * of a .env checklist.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CloudUpload, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/features/auth/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ConfigView = {
    enabled: boolean;
    credentials_configured: boolean;
    credentials_preview: string | null;
    api_key: string | null;
    auth_domain: string | null;
    project_id: string | null;
    storage_bucket: string | null;
    messaging_sender_id: string | null;
    app_id: string | null;
    vapid_key_configured: boolean;
};

export default function AdminFirebase() {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ["admin", "firebase"],
        queryFn: async () => (await api.get<ConfigView>("/admin/settings/firebase")).data,
    });

    const [enabled, setEnabled] = useState(false);
    const [creds, setCreds] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [authDomain, setAuthDomain] = useState("");
    const [projectId, setProjectId] = useState("");
    const [storageBucket, setStorageBucket] = useState("");
    const [senderId, setSenderId] = useState("");
    const [appId, setAppId] = useState("");
    const [vapid, setVapid] = useState("");

    useEffect(() => {
        if (!data) return;
        setEnabled(data.enabled);
        setApiKey(data.api_key || "");
        setAuthDomain(data.auth_domain || "");
        setProjectId(data.project_id || "");
        setStorageBucket(data.storage_bucket || "");
        setSenderId(data.messaging_sender_id || "");
        setAppId(data.app_id || "");
    }, [data]);

    const saveMut = useMutation({
        mutationFn: async () =>
            api.put("/admin/settings/firebase", {
                enabled,
                credentials_json: creds || undefined,
                api_key: apiKey || undefined,
                auth_domain: authDomain || undefined,
                project_id: projectId || undefined,
                storage_bucket: storageBucket || undefined,
                messaging_sender_id: senderId || undefined,
                app_id: appId || undefined,
                vapid_key: vapid || undefined,
            }),
        onSuccess: () => {
            toast.success("Configuration saved");
            setCreds("");
            setVapid("");
            qc.invalidateQueries({ queryKey: ["admin", "firebase"] });
        },
        onError: (e: unknown) => toast.error((e as Error).message || "Save failed"),
    });

    const testMut = useMutation({
        mutationFn: async () => (await api.post("/admin/settings/firebase/test")).data,
        onSuccess: (d: { delivered: boolean }) =>
            d.delivered
                ? toast.success("Test push sent — check your device")
                : toast.warning("Firebase configured but no registered devices for your account"),
        onError: (e: unknown) => toast.error((e as Error).message || "Test push failed"),
    });

    return (
        <div data-testid="admin-firebase" className="space-y-4">
            <header className="flex items-center gap-2">
                <CloudUpload className="h-5 w-5 text-brand" />
                <h1 className="text-2xl font-extrabold">Firebase Web Push</h1>
            </header>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-bold">Status</p>
                        <p className="text-xs text-muted-foreground">
                            {data?.credentials_configured
                                ? `Service account: ${data.credentials_preview}`
                                : "No service account configured — push is disabled."}
                        </p>
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            data-testid="firebase-enabled"
                        />
                        Enabled
                    </label>
                </div>
            </section>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">Backend — service account</h3>
                <p className="text-xs text-muted-foreground">
                    Paste the ENTIRE Firebase Admin service-account JSON. Stored server-side; never
                    re-emitted to the browser after saving.
                </p>
                <textarea
                    className="mt-2 min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                    placeholder={data?.credentials_configured ? "Paste to replace…" : '{"type":"service_account",...}'}
                    value={creds}
                    onChange={(e) => setCreds(e.target.value)}
                    data-testid="firebase-credentials"
                />
            </section>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">Frontend — Web App config</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Field label="apiKey" v={apiKey} set={setApiKey} testid="firebase-api-key" />
                    <Field label="authDomain" v={authDomain} set={setAuthDomain} testid="firebase-auth-domain" />
                    <Field label="projectId" v={projectId} set={setProjectId} testid="firebase-project-id" />
                    <Field label="storageBucket" v={storageBucket} set={setStorageBucket} testid="firebase-storage-bucket" />
                    <Field label="messagingSenderId" v={senderId} set={setSenderId} testid="firebase-sender-id" />
                    <Field label="appId" v={appId} set={setAppId} testid="firebase-app-id" />
                    <div className="sm:col-span-2">
                        <Label>VAPID key {data?.vapid_key_configured ? "(currently configured)" : ""}</Label>
                        <Input
                            value={vapid}
                            onChange={(e) => setVapid(e.target.value)}
                            data-testid="firebase-vapid-key"
                            placeholder={data?.vapid_key_configured ? "Paste to replace…" : ""}
                        />
                    </div>
                </div>
            </section>

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => saveMut.mutate()}
                    disabled={saveMut.isPending}
                    data-testid="firebase-save"
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                >
                    <ShieldCheck className="h-4 w-4" /> Save configuration
                </button>
                <button
                    type="button"
                    onClick={() => testMut.mutate()}
                    disabled={testMut.isPending || !data?.credentials_configured}
                    data-testid="firebase-test"
                    className="inline-flex items-center gap-2 rounded-full border border-border/70 px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-50"
                >
                    <Send className="h-4 w-4" /> Send test push
                </button>
            </div>

            <p className="text-xs text-muted-foreground">
                Only Super Admin can view or edit this screen. The service-account private key is
                never exposed to riders, store owners, staff or customers.
            </p>
        </div>
    );
}

function Field({
    label,
    v,
    set,
    testid,
}: {
    label: string;
    v: string;
    set: (s: string) => void;
    testid: string;
}) {
    return (
        <div>
            <Label>{label}</Label>
            <Input value={v} onChange={(e) => set(e.target.value)} data-testid={testid} />
        </div>
    );
}
